import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  TestTube, 
  AlertTriangle, 
  Database, 
  Monitor, 
  BarChart3, 
  Users, 
  Clock, 
  Shield,
  Activity,
  ArrowRight,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useABTesting } from '@/hooks/ab-testing/useABTesting';
import { useAlerting } from '@/hooks/alerts/useAlerting';
import { useBackup } from '@/hooks/backup/useBackup';
import EnterpriseAdEconomy from './EnterpriseAdEconomy';

export const EnterpriseDashboard: React.FC = () => {
  const { experiments, loading: abLoading } = useABTesting();
  const { alertStats, activeAlerts, loading: alertsLoading } = useAlerting();
  const { backupStats, runningJobs, loading: backupLoading } = useBackup();

  const enterpriseFeatures = [
    {
      id: 'ab-testing',
      title: 'A/B Testing',
      description: 'Eksperimenter med ulike versjoner for å optimalisere brukeropplevelsen',
      icon: TestTube,
      path: '/ab-testing',
      stats: [
        { label: 'Aktive tester', value: experiments.filter(e => e.status === 'active').length },
        { label: 'Totalt tester', value: experiments.length }
      ],
      status: abLoading ? 'loading' : experiments.length > 0 ? 'active' : 'inactive',
      color: 'bg-blue-500'
    },
    {
      id: 'alerts',
      title: 'Overvåkning & Varsler',
      description: 'Automatisk overvåkning av systemytelse og feildeteksjon',
      icon: AlertTriangle,
      path: '/alerts',
      stats: [
        { label: 'Aktive varsler', value: alertStats.active_alerts },
        { label: 'Kritiske varsler', value: alertStats.critical_alerts }
      ],
      status: alertsLoading ? 'loading' : activeAlerts.length > 0 ? 'warning' : 'active',
      color: 'bg-orange-500'
    },
    {
      id: 'backup',
      title: 'Sikkerhetskopi',
      description: 'Automatisk sikkerhetskopi og gjenoppretting av data',
      icon: Database,
      path: '/backup',
      stats: [
        { label: 'Aktive jobber', value: runningJobs.length },
        { label: 'Lagring brukt', value: formatBytes(backupStats.storage_used) }
      ],
      status: backupLoading ? 'loading' : backupStats.total_backups > 0 ? 'active' : 'inactive',
      color: 'bg-green-500'
    },
    {
      id: 'monitoring',
      title: 'Systemovervåkning',
      description: 'Detaljert overvåkning av systemytelse og brukeraktivitet',
      icon: Monitor,
      path: '/monitoring',
      stats: [
        { label: 'Oppetid', value: '99.9%' },
        { label: 'Responstid', value: '< 200ms' }
      ],
      status: 'active',
      color: 'bg-purple-500'
    },
    {
      id: 'analytics',
      title: 'Analytikk & Innsikt',
      description: 'Avansert dataanalyse og brukerinnsikt',
      icon: BarChart3,
      path: '/analytics',
      stats: [
        { label: 'Brukere i dag', value: '2,341' },
        { label: 'Sidevisninger', value: '12,847' }
      ],
      status: 'active',
      color: 'bg-indigo-500'
    }
  ];

  function formatBytes(bytes: number) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="h-3 w-3 mr-1" />Aktiv</Badge>;
      case 'warning':
        return <Badge className="bg-orange-100 text-orange-800 border-orange-200"><AlertTriangle className="h-3 w-3 mr-1" />Advarsler</Badge>;
      case 'inactive':
        return <Badge variant="secondary"><XCircle className="h-3 w-3 mr-1" />Inaktiv</Badge>;
      case 'loading':
        return <Badge variant="outline"><Activity className="h-3 w-3 mr-1 animate-spin" />Laster...</Badge>;
      default:
        return <Badge variant="outline">Ukjent</Badge>;
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-foreground">Enterprise Dashboard</h1>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Administrer og overvåk alle enterprise-funksjoner fra ett sted. 
          Få innsikt i systemytelse, kjør eksperimenter, og sikre dataene dine.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Aktive Tester</CardTitle>
            <TestTube className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{experiments.filter(e => e.status === 'active').length}</div>
            <p className="text-xs text-muted-foreground">A/B eksperimenter kjører</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Aktive Varsler</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{alertStats.active_alerts}</div>
            <p className="text-xs text-muted-foreground">Krever oppmerksomhet</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Sikkerhetskopi</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{backupStats.total_backups}</div>
            <p className="text-xs text-muted-foreground">Fullførte sikkerhetskopier</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Systemstatus</CardTitle>
            <Monitor className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">99.9%</div>
            <p className="text-xs text-muted-foreground">Oppetid siste 30 dager</p>
          </CardContent>
        </Card>
      </div>

      {/* Feature Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {enterpriseFeatures.map((feature) => {
          const Icon = feature.icon;
          return (
            <Card key={feature.id} className="relative overflow-hidden">
              <div className={`absolute top-0 left-0 w-full h-1 ${feature.color}`} />
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${feature.color} bg-opacity-10`}>
                      <Icon className={`h-5 w-5 text-${feature.color.split('-')[1]}-600`} />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                    </div>
                  </div>
                  {getStatusBadge(feature.status)}
                </div>
                <CardDescription className="text-sm">
                  {feature.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-2 gap-4">
                  {feature.stats.map((stat, index) => (
                    <div key={index} className="text-center">
                      <div className="text-xl font-bold text-foreground">{stat.value}</div>
                      <div className="text-xs text-muted-foreground">{stat.label}</div>
                    </div>
                  ))}
                </div>
                
                {/* Action Button */}
                <Button asChild className="w-full" variant="outline">
                  <Link to={feature.path} className="flex items-center justify-center gap-2">
                    Åpne Dashboard
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Siste Aktivitet
          </CardTitle>
          <CardDescription>
            Oversikt over nylige endringer og aktiviteter på tvers av alle enterprise-funksjoner
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {runningJobs.length > 0 && (
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <Database className="h-4 w-4 text-blue-600" />
                <div className="flex-1">
                  <div className="font-medium">Sikkerhetskopi kjører</div>
                  <div className="text-sm text-muted-foreground">
                    {runningJobs.length} aktive backup-jobber
                  </div>
                </div>
                <Badge variant="outline">Pågår</Badge>
              </div>
            )}
            
            {activeAlerts.length > 0 && (
              <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <div className="flex-1">
                  <div className="font-medium">Nye varsler</div>
                  <div className="text-sm text-muted-foreground">
                    {activeAlerts.length} aktive varsler krever oppmerksomhet
                  </div>
                </div>
                <Badge variant="outline">Krever handling</Badge>
              </div>
            )}
            
            {experiments.filter(e => e.status === 'active').length > 0 && (
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <TestTube className="h-4 w-4 text-green-600" />
                <div className="flex-1">
                  <div className="font-medium">A/B tester kjører</div>
                  <div className="text-sm text-muted-foreground">
                    {experiments.filter(e => e.status === 'active').length} aktive eksperimenter
                  </div>
                </div>
                <Badge variant="outline">Aktiv</Badge>
              </div>
            )}

            {runningJobs.length === 0 && activeAlerts.length === 0 && experiments.filter(e => e.status === 'active').length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="h-8 w-8 mx-auto mb-2" />
                <div>Ingen pågående aktiviteter</div>
                <div className="text-sm">Alle systemer kjører normalt</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Annonseøkonomi */}
      <EnterpriseAdEconomy />
    </div>
  );
};